/**
 * This component will show the booking info and calculated total price.
 * I.e. dates and other details related to payment decision in receipt format.
 */
import React from 'react';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { getDefaultTimeZoneOnBrowser } from '../../util/dates';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  DATE_TYPE_DATE,
  DATE_TYPE_DATETIME,
  DATE_TYPE_TIME,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_FIXED,
  LINE_ITEM_HOUR,
  LINE_ITEM_PROVIDER_COMMISSION,
  LISTING_UNIT_TYPES,
  propTypes,
} from '../../util/types';
import {
  EN_TEACHING_CATEGORY_LABELS,
  AR_TEACHING_CATEGORY_LABELS,
  normalizeCategoryLookupKey,
  humanizeCategoryValue,
} from '../../util/helpersFunction';

import LineItemBookingPeriod from './LineItemBookingPeriod';
import LineItemBasePriceMaybe from './LineItemBasePriceMaybe';
import LineItemSubTotalMaybe from './LineItemSubTotalMaybe';
import LineItemShippingFeeMaybe from './LineItemShippingFeeMaybe';
import LineItemPickupFeeMaybe from './LineItemPickupFeeMaybe';
import LineItemCustomerCommissionMaybe from './LineItemCustomerCommissionMaybe';
import LineItemCustomerCommissionRefundMaybe from './LineItemCustomerCommissionRefundMaybe';
import LineItemProviderCommissionMaybe from './LineItemProviderCommissionMaybe';
import LineItemProviderCommissionRefundMaybe from './LineItemProviderCommissionRefundMaybe';
import LineItemRefundMaybe from './LineItemRefundMaybe';
import LineItemTotalPrice from './LineItemTotalPrice';
import LineItemUnknownItemsMaybe from './LineItemUnknownItemsMaybe';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const categoryLabelFromValue = (value, locale) => {
  if (!value) {
    return null;
  }

  const key = normalizeCategoryLookupKey(value);
  const isArabicLocale = String(locale || '').toLowerCase().startsWith('ar');
  const localized = isArabicLocale
    ? AR_TEACHING_CATEGORY_LABELS[key]
    : EN_TEACHING_CATEGORY_LABELS[key];

  return localized || humanizeCategoryValue(value);
};

export const OrderBreakdownComponent = props => {
  const {
    rootClassName,
    className,
    userRole,
    transaction,
    booking,
    timeZone,
    currency,
    marketplaceName,
    intl,
  } = props;

  const isCustomer = userRole === 'customer';
  const isProvider = userRole === 'provider';
  const allLineItems = transaction.attributes.lineItems || [];
  const isTrialBooking =
    transaction?.attributes?.protectedData?.isTrialBooking === true ||
    transaction?.attributes?.metadata?.bookingType === 'trial';
  // We'll show only line-items that are specific for the current userRole (customer vs provider)
  const rawLineItems = allLineItems.filter(lineItem => lineItem.includeFor.includes(userRole));
  const lineItems = isTrialBooking
    ? rawLineItems.map(li => {
        const currencyCode = li?.unitPrice?.currency || li?.lineTotal?.currency || currency;
        if (!currencyCode) return li;
        const zero = new Money(0, currencyCode);
        return {
          ...li,
          unitPrice: li.unitPrice ? zero : li.unitPrice,
          lineTotal: li.lineTotal ? zero : li.lineTotal,
        };
      })
    : rawLineItems;

  const totalCurrency =
    transaction?.attributes?.payinTotal?.currency ||
    transaction?.attributes?.payoutTotal?.currency ||
    lineItems?.[0]?.unitPrice?.currency ||
    lineItems?.[0]?.lineTotal?.currency ||
    currency;
  const zeroTotal = totalCurrency ? new Money(0, totalCurrency) : null;
  const transactionForTotal =
    isTrialBooking && zeroTotal
      ? {
          ...transaction,
          attributes: {
            ...transaction.attributes,
            payinTotal: zeroTotal,
            payoutTotal: zeroTotal,
          },
        }
      : transaction;
  const unitLineItem = lineItems.find(
    item => LISTING_UNIT_TYPES.includes(item.code) && !item.reversal
  );
  // Line-item code that matches with base unit: day, night, hour, fixed, item
  const lineItemUnitType = unitLineItem?.code;
  const dateType = [LINE_ITEM_HOUR, LINE_ITEM_FIXED].includes(lineItemUnitType)
    ? DATE_TYPE_DATETIME
    : DATE_TYPE_DATE;

  const hasCommissionLineItem = lineItems.find(item => {
    const hasCustomerCommission = isCustomer && item.code === LINE_ITEM_CUSTOMER_COMMISSION;
    const hasProviderCommission = isProvider && item.code === LINE_ITEM_PROVIDER_COMMISSION;
    return (hasCustomerCommission || hasProviderCommission) && !item.reversal;
  });

  const classes = classNames(rootClassName || css.root, className);

  const lastTransition = transaction?.attributes?.lastTransition;
  const isTutorCancel = [
    'transition/cancel',
    'transition/cancel-from-interview',
    'transition/cancel-by-provider',
  ].includes(lastTransition);

  const tutorCancelNotice = isCustomer && isTutorCancel && transaction.attributes.state === 'canceled' ? (
    <div className={css.tutorCancelNotice}>
      <FormattedMessage id="OrderBreakdown.tutorCancelNotice" />
    </div>
  ) : null;
  const selectedTeachingCategory = transaction?.attributes?.protectedData?.selectedTeachingCategory;
  
  // Handle both old string format and new object format {Key, Label}
  // Always use the Key to look up the label in the current locale
  const categoryKey = typeof selectedTeachingCategory === 'object' && selectedTeachingCategory?.Key
    ? selectedTeachingCategory.Key
    : selectedTeachingCategory;
  
  const selectedTeachingCategoryLabel = categoryLabelFromValue(categoryKey, intl?.locale);
  
  const categoryLabelText = String(intl?.locale || '').toLowerCase().startsWith('ar')
    ? 'الفئة'
    : 'Category';

  // Display timezone: customer sees times in their local timezone; provider sees listing timezone
  const displayTimeZone =
    userRole === 'customer' && typeof window !== 'undefined'
      ? getDefaultTimeZoneOnBrowser()
      : timeZone;

  /**
   * OrderBreakdown contains different line items:
   *
   * LineItemBookingPeriod: prints booking start and booking end types. Prop dateType
   * determines if the date and time or only the date is shown
   *
   * LineItemShippingFeeMaybe: prints the shipping fee (combining additional fee of
   * additional items into it).
   *
   * LineItemShippingFeeRefundMaybe: prints the amount of refunded shipping fee
   *
   * LineItemBasePriceMaybe: prints the base price calculation for the listing, e.g.
   * "$150.00 * 2 nights $300"
   *
   * LineItemUnknownItemsMaybe: prints the line items that are unknown. In ideal case there
   * should not be unknown line items. If you are using custom pricing, you should create
   * new custom line items if you need them.
   *
   * LineItemSubTotalMaybe: prints subtotal of line items before possible
   * commission or refunds
   *
   * LineItemRefundMaybe: prints the amount of refund
   *
   * LineItemCustomerCommissionMaybe: prints the amount of customer commission
   * The default transaction process used by this template doesn't include the customer commission.
   *
   * LineItemCustomerCommissionRefundMaybe: prints the amount of refunded customer commission
   *
   * LineItemProviderCommissionMaybe: prints the amount of provider commission
   *
   * LineItemProviderCommissionRefundMaybe: prints the amount of refunded provider commission
   *
   * LineItemTotalPrice: prints total price of the transaction
   *
   */

  // Resolve rescheduled booking dates if present in metadata
  const rescheduledStart = transaction?.attributes?.metadata?.zoomSyncData?.scheduledStart;
  const rescheduledEnd = transaction?.attributes?.metadata?.zoomSyncData?.scheduledEnd;

  const displayBooking = rescheduledStart && rescheduledEnd ? {
    ...booking,
    attributes: {
      ...booking?.attributes,
      start: new Date(rescheduledStart),
      end: new Date(rescheduledEnd),
      displayStart: new Date(rescheduledStart),
      displayEnd: new Date(rescheduledEnd),
    }
  } : booking;

  return (
    <div className={classes}>
      {tutorCancelNotice}
      <LineItemBookingPeriod
        booking={displayBooking}
        code={lineItemUnitType}
        dateType={dateType}
        timeZone={displayTimeZone}
      />
      {selectedTeachingCategoryLabel ? (
        <div className={css.lineItem}>
          <span className={css.itemLabel}>{categoryLabelText}</span>
          <span className={css.itemValue}>{selectedTeachingCategoryLabel}</span>
        </div>
      ) : null}

      <LineItemBasePriceMaybe lineItems={lineItems} code={lineItemUnitType} userRole={userRole} intl={intl} />
      <LineItemShippingFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemPickupFeeMaybe lineItems={lineItems} intl={intl} />
      <LineItemUnknownItemsMaybe lineItems={lineItems} isProvider={isProvider} intl={intl} />

      <LineItemSubTotalMaybe
        lineItems={lineItems}
        code={lineItemUnitType}
        userRole={userRole}
        intl={intl}
        marketplaceCurrency={currency}
      />
      <LineItemRefundMaybe lineItems={lineItems} userRole={userRole} intl={intl} marketplaceCurrency={currency} />

      <LineItemCustomerCommissionMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemCustomerCommissionRefundMaybe
        lineItems={lineItems}
        isCustomer={isCustomer}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemProviderCommissionMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />
      <LineItemProviderCommissionRefundMaybe
        lineItems={lineItems}
        isProvider={isProvider}
        marketplaceName={marketplaceName}
        intl={intl}
      />

      <LineItemTotalPrice transaction={transactionForTotal} isProvider={isProvider} intl={intl} />
      <hr className={css.totalDivider} />
      {hasCommissionLineItem ? (
        <span className={css.feeInfo}>
          <FormattedMessage id="OrderBreakdown.commissionFeeNote" />
        </span>
      ) : null}
    </div>
  );
};

/**
 * Order breakdown aka receipt
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to component's own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {string} props.marketplaceName
 * @param {string} props.timeZone IANA time zone name
 * @param {string} props.currency E.g. 'USD'
 * @param {'customer' | 'provider'} props.userRole
 * @param {propTypes.transaction} props.transaction
 * @param {propTypes.booking?} props.booking
 * @param {DATE_TYPE_DATE | DATE_TYPE_TIME | DATE_TYPE_DATETIME} props.dateType
 * @returns {JSX.Element} the order breakdown component
 */
const OrderBreakdown = props => {
  const intl = useIntl();
  return <OrderBreakdownComponent intl={intl} {...props} />;
};

export default OrderBreakdown;
